'use client';

import Link from 'next/link';
import Image from 'next/image';
import classes from './HorizontalImageCard.module.scss';
import { Button, Container, Grid } from '@mantine/core';
import { useAOS } from '@/src/lib/hooks/useAOS';
import { Carousel } from '@mantine/carousel';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import React, { useMemo } from 'react';
import { AppButton } from '@/src/components/AppButton/AppButton';

type Tone = 'default' | 'kids';

type HorizontalImageCardUI = {
  variant?: 'light' | 'dark';
  showSeparator?: boolean;
  bgColor?: string;

  tone?: Tone;
  toneSeed?: string;

  height?: string;
  imageHeight?: string;

  imageFit?: 'cover' | 'contain';

  enableScroll?: boolean;

  /** Optional top-left sticker/tag (e.g. "NEW") */
  tagText?: string;
};

export type HorizontalImageCardProps = {
  type?: 'horizontal-image-card' | 'classic-horizontal-image-card';
  title: string;
  description: string | string[];
  button?: string;
  buttonHref?: string;
  position: 'left' | 'right';
  images: CardImage[];
  imageAlt?: string;

  tone?: Tone;

  ui?: HorizontalImageCardUI;
};

type CardImage = {
  src: string;
  alt: string;
};

// deterministic hash -> 0..1
function hash01(input: string) {
  let h = 2166136261;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return ((h >>> 0) % 10000) / 10000;
}

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}

function normalizeHex(input?: string) {
  if (!input) return '';
  const v = input.trim();
  if (!v) return '';
  return v.startsWith('#') ? v : `#${v}`;
}

function hexToRgb(hexInput?: string): { r: number; g: number; b: number } | null {
  const hex = normalizeHex(hexInput).replace('#', '');
  if (![3, 6].includes(hex.length)) return null;
  const full = hex.length === 3 ? hex.split('').map((c) => c + c).join('') : hex;
  const int = Number.parseInt(full, 16);
  if (Number.isNaN(int)) return null;
  return { r: (int >> 16) & 255, g: (int >> 8) & 255, b: int & 255 };
}

function rgbToHsl(r: number, g: number, b: number) {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;

  let h = 0;
  if (d !== 0) {
    if (max === r) h = ((g - b) / d) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  const l = (max + min) / 2;
  const s = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1));
  return { h, s, l };
}

function hslToRgb(h: number, s: number, l: number) {
  const C = (1 - Math.abs(2 * l - 1)) * s;
  const X = C * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - C / 2;

  let r1 = 0, g1 = 0, b1 = 0;
  if (0 <= h && h < 60) { r1 = C; g1 = X; b1 = 0; }
  else if (60 <= h && h < 120) { r1 = X; g1 = C; b1 = 0; }
  else if (120 <= h && h < 180) { r1 = 0; g1 = C; b1 = X; }
  else if (180 <= h && h < 240) { r1 = 0; g1 = X; b1 = C; }
  else if (240 <= h && h < 300) { r1 = X; g1 = 0; b1 = C; }
  else { r1 = C; g1 = 0; b1 = X; }

  return {
    r: Math.round((r1 + m) * 255),
    g: Math.round((g1 + m) * 255),
    b: Math.round((b1 + m) * 255),
  };
}

function rgbToCss(rgb: { r: number; g: number; b: number }) {
  return `${rgb.r} ${rgb.g} ${rgb.b}`;
}

/**
 * Palette derived ONLY from ui.bgColor:
 * - main = bgColor itself (adjusted a bit for paint look)
 * - accent/highlight derived from same hue, shifted slightly for richness
 */
function paletteFromBgColorOnly(bgColor: string, seed: number) {
  const base = hexToRgb(bgColor);
  if (!base) return null;

  const { h, s, l } = rgbToHsl(base.r, base.g, base.b);

  // keep same “identity” hue, just drift slightly so multiple cards differ
  const drift = (seed - 0.5) * 18; // subtle drift
  const h1 = (h + drift + 360) % 360;

  // make it paint-like (more saturated, a bit lighter)
  const s1 = clamp(Math.max(s, 0.55) + 0.18, 0.55, 0.95);
  const l1 = clamp(Math.max(l, 0.50) + 0.10, 0.52, 0.88);

  const main = hslToRgb(h1, s1, l1);
  const accent = hslToRgb((h1 + 10) % 360, clamp(s1 + 0.04, 0.55, 0.98), clamp(l1 - 0.10, 0.40, 0.80));
  const highlight = hslToRgb((h1 + 2) % 360, clamp(s1 - 0.16, 0.35, 0.9), clamp(l1 + 0.08, 0.60, 0.96));

  return { main, accent, highlight };
}

export const HorizontalImageCard = ({ data }: { data: HorizontalImageCardProps }) => {
  useAOS();

  const { title, description, button, buttonHref, position, images, ui } = data;

  const variant = ui?.variant ?? 'light';
  const showSeparator = ui?.showSeparator ?? true;

  const height = ui?.height ?? '520px';
  const imageHeight = ui?.imageHeight ?? height;

  const enableScroll = ui?.enableScroll ?? true;

  const tone: Tone = (data.tone ?? ui?.tone ?? 'default') as Tone;

  const tagText = ui?.tagText?.trim();

  const bgColor = ui?.bgColor; // ✅ only source for paint colors
  const seedKey = ui?.toneSeed ?? `${title}-${images?.[0]?.src ?? ''}`;
  const seed = useMemo(() => hash01(seedKey), [seedKey]);

  // ✅ palette ONLY if bgColor exists
  const palette = useMemo(() => (bgColor ? paletteFromBgColorOnly(bgColor, seed) : null), [bgColor, seed]);

  const paint = useMemo(() => {
    const a = seed;
    const b = hash01(seedKey + '_b');
    const c = hash01(seedKey + '_c');

    return {
      p1x: `${Math.round(6 + a * 18)}%`,
      p1y: `${Math.round(8 + b * 10)}%`,
      p2x: `${Math.round(76 + c * 16)}%`,
      p2y: `${Math.round(6 + a * 12)}%`,
      p3x: `${Math.round(64 + b * 18)}%`,
      p3y: `${Math.round(72 + c * 18)}%`,
      rot1: `${Math.round(-12 + a * 26)}deg`,
      rot2: `${Math.round(-10 + b * 22)}deg`,
      rot3: `${Math.round(-8 + c * 18)}deg`,
      sc1: `${(0.92 + a * 0.28).toFixed(2)}`,
      sc2: `${(0.90 + b * 0.30).toFixed(2)}`,
      sc3: `${(0.88 + c * 0.32).toFixed(2)}`,
    };
  }, [seed, seedKey]);

  const styleVars: React.CSSProperties = useMemo(() => {
    const vars: any = {};

    // We DO NOT set wrapper background to bgColor
    // bgColor is only used to compute paint colors.
    if (tone === 'kids' && palette) {
      vars['--k-main'] = rgbToCss(palette.main);
      vars['--k-accent'] = rgbToCss(palette.accent);
      vars['--k-hi'] = rgbToCss(palette.highlight);

      vars['--p1x'] = paint.p1x;
      vars['--p1y'] = paint.p1y;
      vars['--p2x'] = paint.p2x;
      vars['--p2y'] = paint.p2y;
      vars['--p3x'] = paint.p3x;
      vars['--p3y'] = paint.p3y;

      vars['--r1'] = paint.rot1;
      vars['--r2'] = paint.rot2;
      vars['--r3'] = paint.rot3;

      vars['--s1'] = paint.sc1;
      vars['--s2'] = paint.sc2;
      vars['--s3'] = paint.sc3;
    }

    return vars as React.CSSProperties;
  }, [tone, palette, paint]);

  // show paint only if kids + bgColor exists (as requested)
  const showPaint = tone === 'kids' && !!bgColor && !!palette;

  // sticker look: kids palette if available, otherwise fallback gradient
  const tagVariantClass =
    showPaint ? classes.tagKids : variant === 'dark' ? classes.tagDark : classes.tagLight;

  return (
    <section
      className={[
        classes.wrapper,
        variant === 'dark' ? classes.dark : classes.light,
        showSeparator ? classes.withSeparator : classes.noSeparator,
        showPaint ? classes.kids : '',
      ].join(' ')}
      style={Object.keys(styleVars).length ? styleVars : undefined}
      data-aos="zoom-in"
    >
      {showPaint && <div className={classes.paintLayer} aria-hidden="true" />}

      <Container className={classes.container} size="xl">
        <Grid
          gutter="lg"
          className={classes.grid}
          style={{ flexDirection: position === 'left' ? 'row-reverse' : 'row' }}
        >
          {/* IMAGE */}
          <Grid.Col
            span={{ base: 12, sm: 7 }}
            className={classes.imageCol}
            data-aos="fade"
            order={{ base: 1, sm: position === 'left' ? 2 : 1 }}
          >
            <h2 className={classes.titleMobile}>{title}</h2>

            <div className={classes.imageWrap} style={{ height: imageHeight }}>
              {/* Optional top-left tag (inside image) */}
              {!!tagText && (
                <span className={[classes.tag, tagVariantClass].join(' ')} aria-label={tagText}>
                  {tagText}
                </span>
              )}

              <Carousel
                withIndicators
                emblaOptions={{ loop: true }}
                className={classes.carousel}
                controlSize={44}
                previousControlProps={{ 'aria-label': 'Image précédente' }}
                nextControlProps={{ 'aria-label': 'Image suivante' }}
                previousControlIcon={<IconChevronLeft className={classes.controlIcon} color="#957365" stroke={1.6} />}
                nextControlIcon={<IconChevronRight className={classes.controlIcon} color="#957365" stroke={1.6} />}
              >
                {images.map((img, i) => (
                  <Carousel.Slide key={i}>
                    <div className={classes.slide} data-aos="fade" data-aos-delay={i === 0 ? 0 : 40}>
                      <Image
                        src={img.src}
                        alt={img.alt ?? title}
                        fill
                        sizes="(max-width: 768px) 100vw, 60vw"
                        className={classes.image}
                        priority={i === 0}
                        loading={i === 0 ? 'eager' : 'lazy'}
                      />
                      <span className={classes.sheen} aria-hidden="true" />
                    </div>
                  </Carousel.Slide>
                ))}
              </Carousel>
            </div>
          </Grid.Col>

          {/* TEXT */}
          <Grid.Col
            span={{ base: 12, sm: 5 }}
            className={classes.textCol}
            order={{ base: 2, sm: position === 'left' ? 1 : 2 }}
          >
            <div className={classes.textBox} data-side={position} data-aos="fade-up" data-aos-delay="120">
              <h2 className={classes.title}>{title}</h2>

              <div className={[classes.middle, enableScroll ? classes.scroll : classes.clamp].join(' ')}>
                <div className={classes.description}>
                  {Array.isArray(description)
                    ? description.map((p, i) => (
                      <p key={i} className={classes.paragraph}>
                        {p}
                      </p>
                    ))
                    : <p className={classes.paragraph}>{description}</p>}
                </div>
              </div>

              <div className={classes.bottom} data-aos="fade-up" data-aos-delay="160">
                {button && (
                  <AppButton component={Link} href={buttonHref ?? ''}>
                    {button}
                  </AppButton>
                )}
              </div>
            </div>
          </Grid.Col>
        </Grid>
      </Container>
    </section>
  );
};